import type { ReactNode } from "react";
import { OtsForm, OtsFormActions, OtsFormErrorSummary } from "../components/OtsForm";
import { OtsInlineAlert } from "../components/OtsInlineAlert";
import type { ShiftFormNames, ShiftFormValues } from "./shift-form";
import { ShiftFormFields, type ShiftFormFieldsProps } from "./ShiftFormFields";
import { useShiftFormState } from "./useShiftFormState";

export type ShiftFormProps = Omit<ShiftFormFieldsProps, "state"> & {
  /** An edit changes the stored shift; a new one is created. */
  mode: "new" | "edit";
  initialValues: ShiftFormValues;
  /** Starts the form over from `initialValues` when it changes, e.g. once the stored shift loads. */
  resetKey?: unknown;
  onSubmit: (values: ShiftFormValues, names: ShiftFormNames) => void;
  onCancel?: () => void;
  submitting?: boolean;
  /** A failed save, shown above the fields; the form keeps what was entered. */
  error?: ReactNode;
  /** Holds the form while the stored shift loads. */
  loading?: boolean;
  density?: "compact" | "default";
  className?: string;
};

/** The shift form for a page: grouped sections, with what needs attention summed up above them after a save. */
export const ShiftForm = ({
  mode,
  initialValues,
  resetKey,
  onSubmit,
  onCancel,
  submitting = false,
  error,
  loading = false,
  density = "default",
  className,
  ...fields
}: ShiftFormProps) => {
  const state = useShiftFormState(initialValues, resetKey);
  return (
    <OtsForm
      aria-label={mode === "edit" ? "Edit shift" : "New shift"}
      noValidate
      density={density}
      disabled={submitting || loading}
      className={className}
      onSubmit={() => {
        if (!submitting && !loading && state.validate()) onSubmit(state.values, state.names);
      }}
      alert={<>
        <OtsFormErrorSummary errors={state.errors}/>
        {error != null && error !== false && <OtsInlineAlert tone="danger" size={density}>{error}</OtsInlineAlert>}
      </>}
      actions={(
        <OtsFormActions
          submitLabel={mode === "edit" ? "Save shift" : "Create shift"}
          submittingLabel="Saving shift…"
          progressLabel="Saving shift"
          submitting={submitting}
          submitDisabled={loading}
          onCancel={onCancel}/>
      )}>
      <ShiftFormFields {...fields} state={state}/>
    </OtsForm>
  );
};
